import { ProgramAccommodationTypeEnum } from '@koob/enums';
import {
  getDayEffectiveEndPosition,
  getSetting,
  getSettingLabel,
} from '@koob/utils';
import sanitizeHtml from 'sanitize-html';
import { decodeId } from 'utils';
import { dayjs } from '@koob/dates';
import { findBookingForDay } from '../accommodationLabels';
import { getDayStartPosition } from '../dayLabels';
import ExportFlowPage from './ExportFlowPage';
import ExportDayHeader from './ExportDayHeader';
import ExportDayHotel, {
  getHotelStayInfo,
  hasHotelBlock,
} from './ExportDayHotel';
import ExportDayMap from './ExportDayMap';
import ExportDayPois from './ExportDayPois';
import ExportDayProgram from './ExportDayProgram';
import ExportDayServices from './ExportDayServices';
import ExportPage from './ExportPage';
import { sanitizeRichText } from '@koob/ui';

const POI_MAP_WIDTH_PX = 288;
const POI_MAP_HEIGHT_PX = 230;

export default function ExportDay({
  trip,
  tourOperator,
  day,
  index,
  days,
  experiencesMap,
  extraExperiences,
  getExperience,
  experience,
  hotel,
  settings,
  accommodationLabels,
  langSelected,
  theme,
}) {
  const program = experience?.programs?.[day?.experienceDayIndex];
  const startPosition = getDayStartPosition(program, experience);

  // KD-3548 — where the day actually ends once the transfers retained on it are
  // taken into account: on the last day of an experience, a retained transfer
  // carries the traveller somewhere the program never declared. Falls back to
  // the program's own end location on its own, so a day with no transfer prints
  // exactly what it printed before.
  //
  // `program` is passed in rather than derived: the export indexes an
  // experience's programs on a 0-based `experienceDayIndex` where the composer
  // stores it 1-based, and the helper must not have to guess which.
  const effectiveEndPosition = getDayEffectiveEndPosition({
    days,
    dayIndex: index,
    experiencesMap,
    experience,
    program,
  });
  const dayData = {
    trip,
    tourOperator,
    day,
    index,
    experience,
    hotel,
    program,
    settings,
  };
  const hotelId = decodeId(day.hotelId);

  const hotelDayBookings = trip?.bookings
    ?.filter((booking) => decodeId(booking.hotelId) === hotelId)
    .sort((a, b) => new Date(a.startAt) - new Date(b.startAt));
  const currentHotelBooking =
    findBookingForDay(
      hotelDayBookings,
      dayjs(trip?.startDate).add(index, 'days').format('YYYY-MM-DD'),
    ) ??
    hotelDayBookings?.[Math.min(index, (hotelDayBookings?.length ?? 1) - 1)];
  // A night whose price is included in the program still shows the room and
  // meal plan of a booking or room key when there is one.
  const booking = currentHotelBooking;

  // Only the additional services retained on the trip: the program's own
  // included experiences are not a selection and must not be listed here.
  const dayServices = day?.options ?? [];

  const daysSection = getSetting(settings, 'days');
  const programSubsection = getSetting(
    daysSection?.sections,
    'program-of-the-day',
  );
  const accommodationSubsection = getSetting(
    daysSection?.sections,
    'accommodation',
  );
  const storyLabel = getSettingLabel(programSubsection?.fields, 'pod-story');

  // A story with no text — a masked product, or an editor left on an empty
  // paragraph — must not leave its heading alone on the page.
  const story = sanitizeHtml(program?.program ?? '');
  const hasStory =
    sanitizeHtml(story, { allowedTags: [], allowedAttributes: {} }).trim() !==
    '';

  const showHotel = hasHotelBlock({ hotel, day, program });
  const hasPois = !!program?.pois?.length;
  const hasHotelCard =
    !!hotel ||
    program?.accomodationType === ProgramAccommodationTypeEnum.NIGHT_TRANSFER;
  const { nightIndex, totalNights } = getHotelStayInfo(trip?.tripDays, index);
  const nightLabel = day?.hotelBookedOnMyOwn
    ? getSettingLabel(
        accommodationSubsection?.fields,
        'accom-booked-independently',
      )
    : hotel && totalNights > 1
      ? [
          getSettingLabel(accommodationSubsection?.fields, 'accom-night'),
          nightIndex,
          getSettingLabel(accommodationSubsection?.fields, 'accom-night-of'),
          totalNights,
        ].join(' ')
      : null;

  const header = (props) => (
    <ExportDayHeader
      trip={trip}
      index={index}
      program={program}
      experience={experience}
      settings={settings}
      {...props}
    />
  );

  return (
    <>
      <ExportFlowPage
        header={(i) =>
          header({
            compact: i > 0,
            nightLabel: hasHotelCard ? null : nightLabel,
          })
        }
        trip={trip}
        tourOperator={tourOperator}
        settings={settings}
        footerStatus
      >
        <div className="mt-4">
          <ExportDayProgram
            program={program}
            startPosition={startPosition}
            endPosition={effectiveEndPosition}
            settings={settings}
          />
        </div>

        {hasStory && (
          <div className="mt-6">
            <div className="text-2xs font-bold uppercase tracking-widest text-[var(--td-primary)]">
              {storyLabel}
            </div>
            <div
              className="export-day-story flow-root mx-auto mt-2 max-w-[155mm] text-sm leading-[1.75]"
              dangerouslySetInnerHTML={{ __html: sanitizeRichText(story) }}
            />
          </div>
        )}

        <div className="mt-6">
          <ExportDayServices
            services={dayServices}
            extraExperiences={extraExperiences}
            settings={settings}
          />
        </div>
        {!hasHotelCard && showHotel && (
          <div className="mt-6">
            <ExportDayHotel
              trip={trip}
              index={index}
              day={day}
              hotel={hotel}
              booking={booking}
              program={program}
              accommodationLabels={accommodationLabels}
              getExperience={getExperience}
              settings={settings}
              theme={theme}
            />
          </div>
        )}

        {!hasHotelCard && hasPois && (
          <div className="mt-6 grid grid-cols-12 gap-6 items-start">
            <div className="col-span-7 min-w-0">
              <ExportDayPois pois={program?.pois} settings={settings} />
            </div>

            <div className="col-span-5 min-w-0">
              <ExportDayMap
                pois={program?.pois}
                theme={theme}
                width={POI_MAP_WIDTH_PX}
                height={POI_MAP_HEIGHT_PX}
                className="w-full"
                style={{ height: `${POI_MAP_HEIGHT_PX}px` }}
              />
            </div>
          </div>
        )}
      </ExportFlowPage>

      {hasHotelCard && (
        <ExportFlowPage
          header={() => header({ compact: true, nightLabel })}
          trip={trip}
          tourOperator={tourOperator}
          settings={settings}
          footerStatus
        >
          <div className="mt-4">
            <ExportDayHotel
              trip={trip}
              index={index}
              day={day}
              hotel={hotel}
              booking={booking}
              program={program}
              accommodationLabels={accommodationLabels}
              getExperience={getExperience}
              settings={settings}
              theme={theme}
            />
          </div>

          {hasPois && (
            <div className="mt-4 grid grid-cols-12 gap-6 items-start">
              <div className="col-span-7 min-w-0">
                <ExportDayPois pois={program?.pois} settings={settings} />
              </div>

              <div className="col-span-5 min-w-0">
                <ExportDayMap
                  pois={program?.pois}
                  theme={theme}
                  width={POI_MAP_WIDTH_PX}
                  height={POI_MAP_HEIGHT_PX}
                  className="w-full"
                  style={{ height: `${POI_MAP_HEIGHT_PX}px` }}
                />
              </div>
            </div>
          )}
        </ExportFlowPage>
      )}
    </>
  );
}
